import {
  Field,
  makeStyles,
  SpinButton,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import { PageLayout, Section, SectionColumn } from '@powerportalspro/react-fluent';
import { useState } from 'react';

// Same JSON the Blazor `PageLayoutPropertiesDemo` reads from.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Layout.PageLayout.PageLayoutPropertiesDemo';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  spinField: {
    // Compact px-input width: 110px matches the Blazor demo's `Style="width: 110px"`
    // on its FluentNumberField so the two stacks present visually identical toolbars.
    width: '110px',
  },
});

// PageLayout is constrained inside a bounded frame so it doesn't fight the
// docs page's own outer layout. Two cooperating bits make the scrollbars
// appear:
//
//   1. The wrapper uses a definite `height: 200` (not `maxHeight`) +
//      `overflow: hidden`, giving PageLayout a real parent height to
//      anchor percentage heights against.
//
//   2. PageLayout itself is forced into "fill parent" mode via inline
//      `style={{ height: '100%', minHeight: '100%' }}`. PageLayout
//      defaults to `height: 100vh; minHeight: 100vh` because nothing in
//      the sample's React tree provides a `PageLayoutNestingContext` with
//      `isNested: true` (the outer demo site is Blazor; the React sample
//      mounts at the top of its own React tree inside a custom element).
//
// Seven controls drive PageLayout's props directly:
//
//   - `headerVisible` / `navigationVisible` / `footerVisible` toggle each
//     area.
//   - `enableNavigationResizing` shows/hides the splitter handle.
//   - `persistNavigationWidth` opts out of localStorage persistence.
//   - `minNavigationWidth` / `maxNavigationWidth` tune the drag bounds.
//     React's `useResizablePane` reacts to changes live (the clamp callback
//     re-creates on dep change), so dragging right after editing those
//     values respects the new bounds without a remount.
//
// `defaultNavigationWidth` is intentionally omitted from the toolbar — it
// only affects the initial seed + double-click reset target, neither of
// which give the demo user visible feedback while they're already sitting
// inside a mounted, possibly-already-resized PageLayout.
export function PageLayoutPropertiesSample() {
  const styles = useStyles();
  const t = useT();
  const [showHeader, setShowHeader] = useState(true);
  const [showNavigation, setShowNavigation] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [enableNavigationResizing, setEnableNavigationResizing] = useState(true);
  const [persistNavigationWidth, setPersistNavigationWidth] = useState(true);
  const [minNavigationWidth, setMinNavigationWidth] = useState(200);
  const [maxNavigationWidth, setMaxNavigationWidth] = useState(480);
  const padStyle = { padding: '10px' };

  // SpinButton publishes `data.value` as `number | null`; the null branch happens
  // when the user clears the input or types something unparseable. Fall back to
  // the previous value in that case so dragging behavior doesn't crater into
  // NaN bounds.
  function setIntFallback(setter: (n: number) => void, current: number) {
    return (value: number | null | undefined) => {
      if (value === null || value === undefined || !Number.isFinite(value)) return;
      setter(Math.max(0, Math.round(value)));
      void current;
    };
  }

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.show-header`)}
              labelPosition="before"
              checked={showHeader}
              onChange={(_, d) => setShowHeader(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.show-navigation`)}
              labelPosition="before"
              checked={showNavigation}
              onChange={(_, d) => setShowNavigation(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.show-footer`)}
              labelPosition="before"
              checked={showFooter}
              onChange={(_, d) => setShowFooter(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.enable-navigation-resizing`)}
              labelPosition="before"
              checked={enableNavigationResizing}
              onChange={(_, d) => setEnableNavigationResizing(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.persist-navigation-width`)}
              labelPosition="before"
              checked={persistNavigationWidth}
              onChange={(_, d) => setPersistNavigationWidth(d.checked)}
            />
            <Field label={t(`${STRINGS_BASE}.min-navigation-width`)}>
              <SpinButton
                className={styles.spinField}
                value={minNavigationWidth}
                min={0}
                onChange={(_, d) =>
                  setIntFallback(setMinNavigationWidth, minNavigationWidth)(d.value)
                }
              />
            </Field>
            <Field label={t(`${STRINGS_BASE}.max-navigation-width`)}>
              <SpinButton
                className={styles.spinField}
                value={maxNavigationWidth}
                min={50}
                onChange={(_, d) =>
                  setIntFallback(setMaxNavigationWidth, maxNavigationWidth)(d.value)
                }
              />
            </Field>
          </div>
        }
      >
        <div
          style={{
            height: 200,
            overflow: 'hidden',
            border: '1px solid var(--neutral-stroke-rest)',
          }}
        >
          <PageLayout
            style={{ height: '100%', minHeight: '100%' }}
            headerVisible={showHeader}
            navigationVisible={showNavigation}
            footerVisible={showFooter}
            enableNavigationResizing={enableNavigationResizing}
            persistNavigationWidth={persistNavigationWidth}
            minNavigationWidth={minNavigationWidth}
            maxNavigationWidth={maxNavigationWidth}
            headerStyle={padStyle}
            bodyStyle={padStyle}
            navigationStyle={padStyle}
            footerStyle={padStyle}
            header={t(`${STRINGS_BASE}.header-label`)}
            navigation={
              <div style={{ minHeight: 400 }}>
                {t(`${STRINGS_BASE}.navigation-label`)}
              </div>
            }
            body={
              <div style={{ minHeight: 400 }}>{t(`${STRINGS_BASE}.body-label`)}</div>
            }
            footer={t(`${STRINGS_BASE}.footer-label`)}
          />
        </div>
      </SectionColumn>
    </Section>
  );
}
